import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiClient } from '@/App';
import { User, Car, FileText, Check, ArrowLeft, ArrowRight, Loader2, HandshakeIcon } from 'lucide-react';
import { numberToTextRo } from '@/utils/numberToTextRo';

const CONTRACT_SERVICE_ID = 'srv_contract';
// Servicii care necesită pasul "Date contract": orice serviciu care conține "contract" în nume
const isContractService = (serviceId, services) => {
  if (serviceId === 'srv_contract') return true;
  const srv = services.find(s => s.service_id === serviceId);
  return srv && srv.name && srv.name.toLowerCase().includes('contract');
};

const ILFOV_LOCALITIES = [
  "1 Decembrie", "Afumati", "Balotesti", "Berceni", "Bragadiru",
  "Branesti", "Buftea", "Cernica", "Chiajna", "Chitila",
  "Ciolpani", "Ciorogirla", "Clinceni", "Copaceni", "Corbeanca",
  "Cornetu", "Darasti-Ilfov", "Dascalu", "Dobroesti", "Domnesti",
  "Dragomiresti-Vale", "Ganeasa", "Glina", "Gradistea", "Gruiu",
  "Jilava", "Magurele", "Moara Vlasiei", "Mogosoaia", "Nuci",
  "Otopeni", "Pantelimon", "Peris", "Petrachioaia", "Popesti-Leordeni",
  "Snagov", "Stefanestii de Jos", "Tunari", "Vidra", "Voluntari"
];

const OrderForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);

  // Form data
  const [formData, setFormData] = useState({
    client_type: 'persoana_fizica',
    client_name: '',
    cnp_cui: '',
    id_document: '',
    phone: '',
    email: '',
    vehicle_brand: '',
    vehicle_vin: '',
    selectedServices: [],
    terms_accepted: false
  });

  // Contract buyer data (only for "Emitere contract vanzare-cumparare")
  const [contractData, setContractData] = useState({
    buyer_type: 'persoana_fizica',
    buyer_name: '',
    buyer_cnp_cui: '',
    buyer_id_document: '',
    buyer_address: '',
    localitate: '',
    pret_vanzare: '',
    pret_vanzare_moneda: 'EUR'
  });

  const hasContractService = formData.selectedServices.some(s => isContractService(s, services));

  // Dynamically compute steps based on whether contract service is selected
  const stepDefs = useMemo(() => {
    const base = [
      { num: 1, label: 'Date client', icon: User },
      { num: 2, label: 'Date vehicul', icon: Car },
      { num: 3, label: 'Servicii', icon: FileText },
    ];
    if (hasContractService) {
      base.push({ num: 4, label: 'Date contract', icon: HandshakeIcon });
      base.push({ num: 5, label: 'Confirmare', icon: Check });
    } else {
      base.push({ num: 4, label: 'Confirmare', icon: Check });
    }
    return base;
  }, [hasContractService]);

  const maxStep = stepDefs.length;
  const confirmStep = hasContractService ? 5 : 4;
  const contractStep = hasContractService ? 4 : null;

  useEffect(() => {
    fetchServices();

    const servicesParam = searchParams.get('services');
    const serviceParam = searchParams.get('service');

    if (servicesParam) {
      setFormData(prev => ({ ...prev, selectedServices: servicesParam.split(',') }));
    } else if (serviceParam) {
      setFormData(prev => ({ ...prev, selectedServices: [serviceParam] }));
    }
  }, [searchParams]);

  const fetchServices = async () => {
    try {
      const response = await apiClient.get('/services?website=true');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Eroare la incarcarea serviciilor');
    } finally {
      setLoadingServices(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContractChange = (e) => {
    const { name, value } = e.target;
    setContractData(prev => ({ ...prev, [name]: value }));
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(serviceId)
        ? prev.selectedServices.filter(id => id !== serviceId)
        : [...prev.selectedServices, serviceId]
    }));
  };

  const totalPrice = services
    .filter(s => formData.selectedServices.includes(s.service_id))
    .reduce((sum, s) => sum + s.price, 0);

  // Compute price text automatically
  const pretVanzareText = useMemo(() => {
    const num = parseFloat(contractData.pret_vanzare);
    if (!num || num <= 0) return '';
    return `${numberToTextRo(num)} ${contractData.pret_vanzare_moneda}`;
  }, [contractData.pret_vanzare, contractData.pret_vanzare_moneda]);

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!formData.client_name || !formData.cnp_cui || !formData.phone || !formData.email) {
        toast.error('Va rugam sa completati toate campurile obligatorii');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        toast.error('Adresa de email invalida');
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.vehicle_brand || !formData.vehicle_vin) {
        toast.error('Va rugam sa completati datele vehiculului');
        return false;
      }
    }
    if (currentStep === 3) {
      if (formData.selectedServices.length === 0) {
        toast.error('Va rugam sa selectati cel putin un serviciu');
        return false;
      }
    }
    if (currentStep === contractStep) {
      if (!contractData.buyer_name) {
        toast.error('Va rugam sa completati numele cumparatorului');
        return false;
      }
      if (!contractData.buyer_cnp_cui) {
        toast.error('Va rugam sa completati CNP/CUI cumparator');
        return false;
      }
      if (!contractData.localitate) {
        toast.error('Va rugam sa selectati localitatea');
        return false;
      }
      if (!contractData.pret_vanzare || parseFloat(contractData.pret_vanzare) <= 0) {
        toast.error('Va rugam sa completati pretul de vanzare');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, maxStep));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.terms_accepted) {
      toast.error('Trebuie sa acceptati termenii si conditiile');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        client_name: formData.client_name,
        client_type: formData.client_type,
        cnp_cui: formData.cnp_cui,
        id_document: formData.id_document,
        phone: formData.phone,
        email: formData.email,
        vehicle_brand: formData.vehicle_brand,
        vehicle_vin: formData.vehicle_vin,
        services: formData.selectedServices,
        terms_accepted: formData.terms_accepted
      };

      if (hasContractService) {
        payload.contract_data = {
          buyer_type: contractData.buyer_type,
          buyer_name: contractData.buyer_name,
          buyer_cnp_cui: contractData.buyer_cnp_cui,
          buyer_id_document: contractData.buyer_id_document || '',
          buyer_address: contractData.buyer_address || '',
          localitate: contractData.localitate,
          pret_vanzare: parseFloat(contractData.pret_vanzare),
          pret_vanzare_moneda: contractData.pret_vanzare_moneda,
          pret_vanzare_text: pretVanzareText
        };
      }

      const response = await apiClient.post('/orders', payload);

      toast.success('Comanda a fost trimisa cu succes!');
      navigate(`/cont?order=${response.data.order_id}`);
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea comenzii');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="order-form-title">
            Comanda online
          </h1>
          <p className="text-lg text-slate-600">
            Completati formularul pentru a plasa o comanda noua.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-12">
          {stepDefs.map((s, index) => (
            <React.Fragment key={s.num}>
              <div
                className={`flex flex-col items-center ${step >= s.num ? 'text-blue-600' : 'text-slate-400'}`}
                data-testid={`step-indicator-${s.num}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-colors ${
                  step >= s.num ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium hidden sm:block">{s.label}</span>
              </div>
              {index < stepDefs.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 transition-colors ${
                  step > s.num ? 'bg-blue-600' : 'bg-slate-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Card */}
        <Card className="bg-white shadow-lg border-slate-100">
          <CardContent className="p-8">
            {/* Step 1: Client Data */}
            {step === 1 && (
              <div className="form-step space-y-6">
                <div>
                  <Label className="text-slate-700 mb-3 block">Tip client</Label>
                  <RadioGroup
                    value={formData.client_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, client_type: value }))}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="persoana_fizica" id="pf" data-testid="client-type-pf" />
                      <Label htmlFor="pf" className="font-normal cursor-pointer">Persoana fizica</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="persoana_juridica" id="pj" data-testid="client-type-pj" />
                      <Label htmlFor="pj" className="font-normal cursor-pointer">Persoana juridica</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="client_name" className="text-slate-700 mb-2 block">
                      {formData.client_type === 'persoana_fizica' ? 'Nume complet' : 'Denumire firma'} *
                    </Label>
                    <Input
                      id="client_name"
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      placeholder={formData.client_type === 'persoana_fizica' ? 'Ion Popescu' : 'SC Exemplu SRL'}
                      className="h-12"
                      data-testid="input-client-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cnp_cui" className="text-slate-700 mb-2 block">
                      {formData.client_type === 'persoana_fizica' ? 'CNP' : 'CUI'} *
                    </Label>
                    <Input
                      id="cnp_cui"
                      name="cnp_cui"
                      value={formData.cnp_cui}
                      onChange={handleInputChange}
                      placeholder={formData.client_type === 'persoana_fizica' ? '1234567890123' : 'RO12345678'}
                      className="h-12"
                      data-testid="input-cnp-cui"
                    />
                  </div>
                  <div>
                    <Label htmlFor="id_document" className="text-slate-700 mb-2 block">
                      {formData.client_type === 'persoana_fizica' ? 'Serie Numar CI' : 'Nr. Reg. Comertului'}
                    </Label>
                    <Input
                      id="id_document"
                      name="id_document"
                      value={formData.id_document}
                      onChange={handleInputChange}
                      placeholder={formData.client_type === 'persoana_fizica' ? 'ex: RX 123456' : 'ex: J40/1234/2020'}
                      className="h-12"
                      data-testid="input-id-document"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 mb-2 block">Telefon *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="0712 345 678"
                      className="h-12"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700 mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="exemplu@email.com"
                      className="h-12"
                      data-testid="input-email"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Vehicle Data */}
            {step === 2 && (
              <div className="form-step space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="vehicle_brand" className="text-slate-700 mb-2 block">Marca vehicul *</Label>
                    <Input
                      id="vehicle_brand"
                      name="vehicle_brand"
                      value={formData.vehicle_brand}
                      onChange={handleInputChange}
                      placeholder="ex: Volkswagen Golf"
                      className="h-12"
                      data-testid="input-vehicle-brand"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vehicle_vin" className="text-slate-700 mb-2 block">Serie sasiu (VIN) *</Label>
                    <Input
                      id="vehicle_vin"
                      name="vehicle_vin"
                      value={formData.vehicle_vin}
                      onChange={handleInputChange}
                      placeholder="WVWZZZ1JZWW000000"
                      className="h-12"
                      data-testid="input-vehicle-vin"
                    />
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  * Seria de sasiu se gaseste in talonul auto sau pe placuta de identificare a vehiculului.
                </p>
              </div>
            )}

            {/* Step 3: Services Selection */}
            {step === 3 && (
              <div className="form-step space-y-6">
                {loadingServices ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {services.map((service, index) => {
                      const isSelected = formData.selectedServices.includes(service.service_id);
                      return (
                        <div
                          key={service.service_id}
                          onClick={() => toggleService(service.service_id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                          data-testid={`order-service-${index}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleService(service.service_id)}
                                data-testid={`order-service-checkbox-${index}`}
                              />
                              <div>
                                <h4 className="font-medium text-slate-900">{service.name}</h4>
                                <p className="text-sm text-slate-500">{service.description}</p>
                              </div>
                            </div>
                            <span className="text-lg font-bold text-blue-600">{service.price} EUR</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {formData.selectedServices.length > 0 && (
                  <div className="mt-8 p-4 bg-slate-100 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Total estimat:</span>
                      <span className="text-2xl font-bold text-slate-900">{totalPrice} EUR</span>
                    </div>
                    <p className="text-xs text-amber-700 mt-2" data-testid="payment-notice-order">Plata se efectueaza in RON la cursul BNR din ziua platii +1%.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4 (contract): Buyer Data - only when srv_contract is selected */}
            {step === contractStep && hasContractService && (
              <ContractFields
                contractData={contractData}
                setContractData={setContractData}
                handleContractChange={handleContractChange}
                pretVanzareText={pretVanzareText}
              />
            )}

            {/* Confirmation Step */}
            {step === confirmStep && (
              <div className="form-step space-y-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Rezumat comanda</h3>

                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Date client (vanzator)</h4>
                    <p className="text-slate-900">{formData.client_name}</p>
                    <p className="text-slate-600 text-sm">{formData.email} - {formData.phone}</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Vehicul</h4>
                    <p className="text-slate-900">{formData.vehicle_brand}</p>
                    <p className="text-slate-600 text-sm">VIN: {formData.vehicle_vin}</p>
                  </div>

                  {hasContractService && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="contract-summary">
                      <h4 className="text-sm font-medium text-amber-800 mb-3">Date contract vanzare-cumparare</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-slate-500">Tip cumparator:</span>
                          <p className="font-medium">{contractData.buyer_type === 'persoana_fizica' ? 'Persoana fizica' : 'Persoana juridica'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">
                            {contractData.buyer_type === 'persoana_fizica' ? 'Nume cumparator:' : 'Denumire firma:'}
                          </span>
                          <p className="font-medium">{contractData.buyer_name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">
                            {contractData.buyer_type === 'persoana_fizica' ? 'CNP:' : 'CUI:'}
                          </span>
                          <p className="font-medium">{contractData.buyer_cnp_cui}</p>
                        </div>
                        {contractData.buyer_id_document && (
                          <div>
                            <span className="text-slate-500">
                              {contractData.buyer_type === 'persoana_fizica' ? 'Serie Numar CI:' : 'Nr. Reg. Comertului:'}
                            </span>
                            <p className="font-medium">{contractData.buyer_id_document}</p>
                          </div>
                        )}
                        {contractData.buyer_address && (
                          <div className="col-span-2">
                            <span className="text-slate-500">Adresa cumparator:</span>
                            <p className="font-medium">{contractData.buyer_address}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-slate-500">Localitate:</span>
                          <p className="font-medium">{contractData.localitate}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Pret vanzare:</span>
                          <p className="font-medium">{contractData.pret_vanzare} {contractData.pret_vanzare_moneda}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-slate-500">Pret vanzare (text):</span>
                          <p className="font-medium text-amber-900">{pretVanzareText}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Servicii selectate</h4>
                    <ul className="space-y-2">
                      {services
                        .filter(s => formData.selectedServices.includes(s.service_id))
                        .map(service => (
                          <li key={service.service_id} className="flex justify-between">
                            <span className="text-slate-900">{service.name}</span>
                            <span className="text-slate-600">{service.price} EUR</span>
                          </li>
                        ))
                      }
                    </ul>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex justify-between">
                        <span className="font-semibold text-slate-900">Total:</span>
                        <span className="text-xl font-bold text-blue-600">{totalPrice} EUR</span>
                      </div>
                      <p className="text-xs text-amber-700 mt-2">Plata se efectueaza in RON la cursul BNR din ziua platii +1%.</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 mt-6">
                  <Checkbox
                    id="terms"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
                    data-testid="terms-checkbox"
                  />
                  <Label htmlFor="terms" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    Sunt de acord cu{' '}
                    <a href="/termeni-conditii" target="_blank" className="text-blue-600 hover:underline">
                      termenii si conditiile
                    </a>
                    {' '}si{' '}
                    <a href="/politica-confidentialitate" target="_blank" className="text-blue-600 hover:underline">
                      politica de confidentialitate
                    </a>.
                  </Label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-10 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="gap-2"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Inapoi
                </Button>
              ) : (
                <div />
              )}

              {step < maxStep ? (
                <Button
                  onClick={nextStep}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  data-testid="next-step-btn"
                >
                  Continua
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.terms_accepted}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  data-testid="submit-order-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Se trimite...
                    </>
                  ) : (
                    <>
                      Trimite comanda
                      <Check className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

/* ======= Contract Fields Sub-component ======= */
function ContractFields({ contractData, setContractData, handleContractChange, pretVanzareText }) {
  const isBuyerPF = contractData.buyer_type === 'persoana_fizica';

  return (
    <div className="form-step space-y-6" data-testid="contract-fields-section">
      <div className="flex items-center gap-3 mb-2">
        <HandshakeIcon className="h-6 w-6 text-amber-600" />
        <h3 className="text-lg font-semibold text-slate-900">Date contract vanzare-cumparare</h3>
      </div>
      <p className="text-sm text-slate-500 -mt-4">
        Completati datele cumparatorului pentru contractul de vanzare-cumparare.
      </p>

      {/* Buyer Type */}
      <div>
        <Label className="text-slate-700 mb-3 block">Tip cumparator *</Label>
        <Select
          value={contractData.buyer_type}
          onValueChange={(value) => setContractData(prev => ({ ...prev, buyer_type: value, buyer_name: '', buyer_cnp_cui: '', buyer_id_document: '', buyer_address: '' }))}
        >
          <SelectTrigger className="h-12" data-testid="select-buyer-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="persoana_fizica">Persoana fizica</SelectItem>
            <SelectItem value="persoana_juridica">Persoana juridica</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Buyer Name */}
        <div>
          <Label htmlFor="buyer_name" className="text-slate-700 mb-2 block">
            {isBuyerPF ? 'Nume complet cumparator' : 'Denumire firma cumparator'} *
          </Label>
          <Input
            id="buyer_name"
            name="buyer_name"
            value={contractData.buyer_name}
            onChange={handleContractChange}
            placeholder={isBuyerPF ? 'ex: Maria Ionescu' : 'ex: SC Firma SRL'}
            className="h-12"
            data-testid="input-buyer-name"
          />
        </div>

        {/* Buyer CNP/CUI */}
        <div>
          <Label htmlFor="buyer_cnp_cui" className="text-slate-700 mb-2 block">
            {isBuyerPF ? 'CNP cumparator' : 'CUI'} *
          </Label>
          <Input
            id="buyer_cnp_cui"
            name="buyer_cnp_cui"
            value={contractData.buyer_cnp_cui}
            onChange={handleContractChange}
            placeholder={isBuyerPF ? '2951234567890' : 'RO12345678'}
            className="h-12"
            data-testid="input-buyer-cnp-cui"
          />
        </div>

        {/* Buyer ID Document */}
        <div>
          <Label htmlFor="buyer_id_document" className="text-slate-700 mb-2 block">
            {isBuyerPF ? 'Serie Numar CI Cumparator' : 'Nr. Reg. Comertului Cumparator'}
          </Label>
          <Input
            id="buyer_id_document"
            name="buyer_id_document"
            value={contractData.buyer_id_document}
            onChange={handleContractChange}
            placeholder={isBuyerPF ? 'ex: RX 654321' : 'ex: J40/5678/2021'}
            className="h-12"
            data-testid="input-buyer-id-document"
          />
        </div>

        {/* Buyer Address */}
        <div className="md:col-span-2">
          <Label htmlFor="buyer_address" className="text-slate-700 mb-2 block">
            Adresa cumparator
          </Label>
          <Input
            id="buyer_address"
            name="buyer_address"
            value={contractData.buyer_address}
            onChange={handleContractChange}
            placeholder="ex: Str. Exemplu nr. 10, Sector 1, Bucuresti"
            className="h-12"
            data-testid="input-buyer-address"
          />
        </div>

        {/* Localitate */}
        <div>
          <Label htmlFor="localitate" className="text-slate-700 mb-2 block">
            Localitate intocmire document *
          </Label>
          <Select
            value={contractData.localitate}
            onValueChange={(value) => setContractData(prev => ({ ...prev, localitate: value }))}
          >
            <SelectTrigger className="h-12" data-testid="select-localitate">
              <SelectValue placeholder="Selectati localitatea..." />
            </SelectTrigger>
            <SelectContent>
              {ILFOV_LOCALITIES.map((loc) => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">
            Sau scrieti manual:
          </p>
          <Input
            name="localitate"
            value={ILFOV_LOCALITIES.includes(contractData.localitate) ? '' : contractData.localitate}
            onChange={(e) => setContractData(prev => ({ ...prev, localitate: e.target.value }))}
            placeholder="Alta localitate..."
            className="h-10 mt-1"
            data-testid="input-localitate-manual"
          />
        </div>
      </div>

      {/* Price Section */}
      <div className="border-t border-slate-200 pt-6">
        <h4 className="text-base font-medium text-slate-800 mb-4">Pret vanzare vehicul</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-slate-700 mb-2 block">Pret vanzare *</Label>
            <div className="flex gap-3">
              <Input
                name="pret_vanzare"
                type="number"
                min="0"
                step="1"
                value={contractData.pret_vanzare}
                onChange={handleContractChange}
                placeholder="ex: 5000"
                className="h-12 flex-1"
                data-testid="input-pret-vanzare"
              />
              <Select
                value={contractData.pret_vanzare_moneda}
                onValueChange={(value) => setContractData(prev => ({ ...prev, pret_vanzare_moneda: value }))}
              >
                <SelectTrigger className="h-12 w-28" data-testid="select-moneda">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="RON">RON</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-slate-700 mb-2 block">Pret vanzare (text)</Label>
            <div
              className="h-12 flex items-center px-4 bg-amber-50 border border-amber-200 rounded-md text-amber-900 font-medium text-sm"
              data-testid="pret-vanzare-text"
            >
              {pretVanzareText || 'Completati pretul pentru a vedea textul...'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderForm;
